/**
 * O aluno visto pelo profissional (SPEC REL-002).
 *
 * Plano, adesão, feedback e alertas chegam nos Marcos 2 a 4. O que já existe é
 * o perfil esportivo — e o campo de restrições, que é dado de saúde.
 *
 * **Ele aparece com destaque, não escondido.** A tentação seria colocá-lo atrás
 * de um "ver mais": é o dado mais sensível da tela. Mas é também o que muda a
 * prescrição, e um profissional que não o leu prescreve pior. Esconder o que
 * precisa ser lido não protege ninguém — protege quem escondeu.
 */

import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import {
  relationshipErrorKey,
  useChangeStatus,
  useStudent,
} from '@/features/relationships/useRelationships';

export default function StudentDetailPage() {
  const { t } = useTranslation(['relationships', 'profiles', 'plans', 'common']);
  const { relationshipId = '' } = useParams();
  const navigate = useNavigate();
  const { data, isPending, isError, error } = useStudent(relationshipId);
  const change = useChangeStatus();

  if (isPending) {
    return (
      <div className="px-5 py-6" aria-busy="true">
        <p className="text-(--color-sand-700)">{t('common:state.loading')}</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-4 px-5 py-6">
        <Alert tone="error" live="assertive">
          {t(relationshipErrorKey(error))}
        </Alert>
        <Link to="/students" className="mh-button mh-button--ghost text-center">
          {t('common:action.back')}
        </Link>
      </div>
    );
  }

  const chips = (values: string[], group: string) =>
    values.length === 0 ? (
      <span className="text-sm text-(--color-sand-500)">
        {t('relationships:student.notAnswered')}
      </span>
    ) : (
      <span className="flex flex-wrap gap-2">
        {values.map((value) => (
          <span
            key={value}
            className="rounded-full bg-(--color-sand-100) px-3 py-1 text-sm text-(--color-sand-700)"
          >
            {t(`profiles:${group}.${value}`)}
          </span>
        ))}
      </span>
    );

  return (
    <div className="flex flex-col gap-6 px-5 py-6">
      <header>
        <h1 className="text-3xl">{data.display_name}</h1>
        <p className="mt-1 text-(--color-sand-700)">{t(`relationships:status.${data.status}`)}</p>
      </header>

      {/* Os planos vivem sob o vínculo, e o caminho até eles também: é aqui
          que o profissional está quando pensa em prescrever para esta pessoa. */}
      <Link
        to={`/students/${relationshipId}/plans`}
        className="mh-button mh-button--secondary text-center"
      >
        {t('plans:title')}
      </Link>

      {data.restrictions_text ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-(--color-sand-500)">
            {t('relationships:student.restrictions')}
          </h2>
          {/* Tom de aviso, não de erro: é informação para levar em conta, não
              um problema a resolver. */}
          <Alert tone="warning">{data.restrictions_text}</Alert>
        </section>
      ) : null}

      <section className="mh-card flex flex-col gap-4">
        <div>
          <p className="text-sm font-semibold text-(--color-sand-900)">
            {t('profiles:student.goals')}
          </p>
          <div className="mt-2">{chips(data.goals, 'goal')}</div>
        </div>
        <div>
          <p className="text-sm font-semibold text-(--color-sand-900)">
            {t('profiles:student.modalities')}
          </p>
          <div className="mt-2">{chips(data.modalities, 'modality')}</div>
        </div>
        <div>
          <p className="text-sm font-semibold text-(--color-sand-900)">
            {t('profiles:student.level')}
          </p>
          <p className="mt-1 text-sm text-(--color-sand-700)">
            {t(`profiles:level.${data.experience_level}`)}
          </p>
        </div>
        <div>
          <p className="text-sm font-semibold text-(--color-sand-900)">
            {t('profiles:student.minutes')}
          </p>
          <p className="mt-1 text-sm text-(--color-sand-700)" data-numeric>
            {data.available_minutes.length > 0
              ? data.available_minutes.map((value) => `${value} min`).join(' · ')
              : t('relationships:student.notAnswered')}
          </p>
        </div>
        <div>
          <p className="text-sm font-semibold text-(--color-sand-900)">
            {t('profiles:student.locations')}
          </p>
          <div className="mt-2">{chips(data.locations, 'location')}</div>
        </div>
        <div>
          <p className="text-sm font-semibold text-(--color-sand-900)">
            {t('profiles:student.equipment')}
          </p>
          <div className="mt-2">{chips(data.equipment, 'equipment')}</div>
        </div>
      </section>

      {change.isError ? (
        <Alert tone="error" live="assertive">
          {t(relationshipErrorKey(change.error))}
        </Alert>
      ) : null}

      <div className="flex flex-col gap-3">
        <button
          type="button"
          className="mh-button mh-button--secondary"
          disabled={change.isPending}
          onClick={() =>
            change.mutate({
              relationshipId,
              status: data.status === 'paused' ? 'active' : 'paused',
            })
          }
        >
          {data.status === 'paused'
            ? t('relationships:action.resume')
            : t('relationships:action.pause')}
        </button>
        <button
          type="button"
          className="mh-button border-2 border-(--color-danger) bg-transparent text-(--color-danger)"
          disabled={change.isPending}
          // Volta para a lista ao encerrar.
          //
          // Sem isto, a invalidação por prefixo refazia a consulta de detalhe,
          // que agora responde 403 — e a tela exibia um alerta vermelho logo
          // depois de a ação ter funcionado.
          onClick={() =>
            change.mutate(
              { relationshipId, status: 'ended' },
              { onSuccess: () => navigate('/students', { replace: true }) },
            )
          }
        >
          {t('relationships:action.end')}
        </button>
        <p className="text-center text-sm text-(--color-sand-700)">
          {t('relationships:action.endHint')}
        </p>
      </div>
    </div>
  );
}
